/**
 * Shopify Customizer Bridge
 * Handles communication between Shopify theme and React customizer components
 */

class ShopifyCustomizerBridge {
  constructor() {
    this.productId = null;
    this.selectedVariantId = null;
    this.shopDomain = null;
    this.blocks = new Map();
    this.variantPriceMap = new Map();
    this.galleryReplaced = false;
    this.galleryObserver = null;
    this.galleryRestoreTimer = null;
    this.variantParser = null;
    this.isHandlingVariantChange = false; // Reentrancy guard
    this.variantChangeDebounceTimer = null; // Debounce timer for variant changes
    this.productOptionRoles = new Map(); // Cache for detected option roles
    this.currentSelectedLayerId = null; // Track selected layer for quick actions

    this.init();
  }

  init() {
    this.setupEventListeners();
    this.loadProductData();
    this.initLayerQuickActions();
    // initializeBlocks starts from setupEventListeners (DOMContentLoaded or now)
  }

  setupEventListeners() {
    // --- HARD BLOCK native add when customizer is active (capture) ---
    const blockNativeAdd = (ev) => {
      try {
        const f = ev.target?.closest?.('form[action*="/cart/add"]');
        if (!f) return;
        if (this.hasActiveDesign && this.hasActiveDesign()) {
          ev.preventDefault();
          ev.stopImmediatePropagation();
          console.debug("[Bridge] Blocked native add (customizer active)");
          // Kör vår pipeline istället
          const formData = new FormData(f);
          this.handleAddToCart({ formData });
        }
      } catch (_) {}
    };

    // Blockera submit på capture (innan teman hinner lyssna)
    document.addEventListener("submit", blockNativeAdd, true);

    // Blockera klick på add-knapp på capture
    document.addEventListener(
      "click",
      (ev) => {
        const btn = ev.target?.closest?.(
          'form[action*="/cart/add"] [type="submit"], form[action*="/cart/add"] button[name="add"]',
        );
        if (!btn) return;
        blockNativeAdd(ev);
      },
      true,
    );
    // --- END HARD BLOCK ---

    // Listen for Shopify variant changes with reentrancy guard
    document.addEventListener("variant:change", (event) => {
      if (this.isHandlingVariantChange) {
        console.debug("[CustomizerBridge] Skipping variant:change to prevent infinite loop");
        return;
      }
      this.handleVariantChange(event.detail);
    });

    // Listen for customizer variant picker events (support both colon and hyphen)
    const onCustomizerVariantChange = (event) => {
      // Skip if from bridge itself to prevent loops
      if (event.detail?.__fromBridge) {
        console.debug("[CustomizerBridge] Skipping customizer variant change (fromBridge)");
        return;
      }
      // Skip if the native loader is updating to prevent loops
      if (window.CustomizerNativeLoader?.isUpdatingFromCustomizer) {
        console.debug("[CustomizerBridge] Skipping customizer variant change to prevent loop");
        return;
      }
      console.debug("[CustomizerBridge] Received customizer variant change:", event.detail);

      // Handle AI image modify blocks
      document.querySelectorAll('[data-block-type="ai-image-modify"]').forEach((block) => {
        const readyEvent = new CustomEvent("customizer:ready", { detail: { blockType: "ai-image-modify" } });
        block.dispatchEvent(readyEvent);
      });
      this.handleCustomizerVariantChange(event.detail);
    };
    document.addEventListener("customizer:variant:change", onCustomizerVariantChange);
    document.addEventListener("customizer:variant-change", onCustomizerVariantChange);

    document.addEventListener("customizer:product:type:change", (event) => {
      // Skip if from bridge itself to prevent loops
      if (event.detail?.__fromBridge) {
        console.debug("[CustomizerBridge] Skipping product type change (fromBridge)");
        return;
      }
      console.debug("[CustomizerBridge] Received product type change:", event.detail);
      this.sendToTargets("product:type-change", event.detail);

      const sizeLabel = event.detail?.newSize || event.detail?.size;
      if (sizeLabel) {
        this.handleCustomizerVariantChange({
          size: sizeLabel, // kan vara sträng – vår nya findVariantBySize klarar det
          productType: event.detail?.to, // om den finns
          __fromBridge: false,
        });
      }
    });

    // Lägg nära övriga addEventListener i setupEventListeners
    document.addEventListener("customizer:size:change", (e) => {
      console.debug("[CustomizerBridge] Received size change:", e.detail);
      this.handleCustomizerVariantChange(e.detail);
    });
    document.addEventListener("product:size-change", (e) => {
      console.debug("[CustomizerBridge] Received product:size-change:", e.detail);
      this.handleCustomizerVariantChange(e.detail);
    });

    document.addEventListener("customizer:orientation:change", (event) => {
      // Skip if from bridge itself to prevent loops
      if (event.detail?.__fromBridge) {
        console.debug("[CustomizerBridge] Skipping orientation change (fromBridge)");
        return;
      }
      console.debug("[CustomizerBridge] Received orientation change:", event.detail);
      this.sendToTargets("orientation:change", event.detail);
    });

    // Enhanced variant input listener with safer error handling
    const attachVariantInputListener = () => {
      // Listen to the main variant input
      const input = document.querySelector('form[action*="/cart/add"] [name="id"]');
      if (input && !input.dataset.customizerListener) {
        input.addEventListener("change", () => {
          const variantId = input.value;
          this.handleVariantInputChange(variantId);
        });
        input.dataset.customizerListener = "true";
      }

      // Listen to variant-picker inputs (Shopify's native variant selector)
      const variantPickerInputs = document.querySelectorAll(
        'variant-picker input[type="radio"], variant-picker select',
      );
      variantPickerInputs.forEach((input) => {
        if (!input.dataset.customizerListener) {
          input.addEventListener("change", () => {
            console.debug("[CustomizerBridge] Variant picker input changed:", input.name, input.value);
            this.handleVariantPickerChange();
          });
          input.dataset.customizerListener = "true";
        }
      });

      // Listen to variant selector dropdowns and buttons (legacy/fallback)
      const variantSelectors = document.querySelectorAll('[name^="options"], .variant-input, [data-variant-input]');
      variantSelectors.forEach((selector) => {
        if (!selector.dataset.customizerListener) {
          selector.addEventListener("change", () => {
            setTimeout(() => {
              const currentVariantId = document.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
              if (currentVariantId) {
                this.handleVariantInputChange(currentVariantId);
              }
            }, 100); // Small delay to let Shopify update the hidden input
          });
          selector.dataset.customizerListener = "true";
        }
      });

      // Listen to variant buttons (for swatch/button style selectors)
      const variantButtons = document.querySelectorAll("[data-variant-id], .variant-button, .swatch");
      variantButtons.forEach((button) => {
        if (!button.dataset.customizerListener) {
          button.addEventListener("click", () => {
            setTimeout(() => {
              const currentVariantId = document.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
              if (currentVariantId) {
                this.handleVariantInputChange(currentVariantId);
              }
            }, 200); // Longer delay for button-style selectors
          });
          button.dataset.customizerListener = "true";
        }
      });
    };

    // Handle variant-picker changes (for Shopify's native variant selector)
    this.handleVariantPickerChange = async () => {
      // Debounce variant changes to prevent rapid-fire updates
      if (this.variantChangeDebounceTimer) {
        clearTimeout(this.variantChangeDebounceTimer);
      }

      this.variantChangeDebounceTimer = setTimeout(async () => {
        try {
          console.debug("[CustomizerBridge] Processing variant picker change (debounced)");

          // Read selected options directly from variant-picker
          const selectedOptions = this.getSelectedOptionsFromPicker();
          console.debug("[CustomizerBridge] Selected options from picker:", selectedOptions);

          if (!selectedOptions.option1 && !selectedOptions.option2 && !selectedOptions.option3) {
            console.debug("[CustomizerBridge] No options selected");
            return;
          }

          // Skapa en minimal variant-struktur för parsing - kompatibel med parseVariantOptions
          const mockVariant = {
            id: "mock-variant-" + Date.now(), // Lägg till id för att undvika undefined error
            options: [selectedOptions.option1, selectedOptions.option2, selectedOptions.option3].filter(
              (option) => option !== null && option !== undefined,
            ),
          };

          // Parse options to standardized format
          const variantData = this.parseVariantOptions(mockVariant, { options: [] });

          // Send complete normalized payload to iframe including separate events
          if (variantData.productType) {
            document.dispatchEvent(
              new CustomEvent("customizer:product:type-change", {
                detail: { to: variantData.productType, __fromBridge: true },
              }),
            );
          }

          if (variantData.orientation) {
            document.dispatchEvent(
              new CustomEvent("customizer:orientation:change", {
                detail: { to: variantData.orientation, __fromBridge: true },
              }),
            );
          }

          // Send comprehensive variant change with all data
          this.handleVariantChange(variantData, false);
        } catch (e) {
          console.warn("[CustomizerBridge] Error handling variant picker change:", e);
        }
      }, 150); // 150ms debounce delay
    };

    this.handleVariantInputChange = async (variantId) => {
      try {
        console.debug(`[CustomizerBridge] variant input change for variant ${variantId}`);

        // Simply store the variant ID and notify customizer
        this.selectedVariantId = variantId;

        // Notify customizer blocks of the variant ID change
        this.notifyBlocks("variant:id-change", { variantId });
      } catch (e) {
        console.debug("[CustomizerBridge] Error handling variant input change:", e);
      }
    };

    // Enhanced position-based method to parse variant options (language-independent)
    this.parseVariantOptions = (variant, product) => {
      const result = {
        productType: null,
        size: null,
        frame: null,
        orientation: null,
      };

      console.debug("[CustomizerBridge] Parsing variant options (position-based):", { variant, product });

      // Get real product options from DOM if not provided
      const productOptions = product?.options || this.getProductOptionsMetaFromPicker();

      if (variant.options && Array.isArray(variant.options)) {
        // Detect option roles based on value patterns (position-independent)
        const optionRoles = this.detectOptionRoles(variant.options, productOptions);
        console.debug("[CustomizerBridge] Detected option roles:", optionRoles);

        variant.options.forEach((option, index) => {
          const role = optionRoles[index];
          const optionValue = option.toLowerCase();

          console.debug("[CustomizerBridge] Processing option:", { role, optionValue, originalValue: option, index });

          // Parse based on detected role
          switch (role) {
            case "productType":
              result.productType = optionValue.includes("canvas") || optionValue.includes("duk") ? "canvas" : "poster";
              console.debug("[CustomizerBridge] Detected product type:", result.productType);
              break;

            case "size":
              // Enhanced regex to handle various size formats
              const sizeMatch = option.match(/(\d+)\s*[x×xX]\s*(\d+)/);
              if (sizeMatch) {
                const width = parseInt(sizeMatch[1]);
                const height = parseInt(sizeMatch[2]);
                let label = option.trim();
                // Ensure cm suffix for consistency
                if (!label.includes(" cm")) {
                  label = `${label} cm`;
                }
                result.size = { width, height, label };
                result.orientation = width > height ? "landscape" : "portrait";
                console.debug("[CustomizerBridge] Detected size:", result.size);
              }
              break;

            case "frame":
              result.frame = this.normalizeFrameName(optionValue);
              console.debug("[CustomizerBridge] Detected frame:", result.frame, "from:", option);
              break;
          }
        });
      }

      console.debug("[CustomizerBridge] Final parsed result:", result);
      return {
        variant,
        ...result,
      };
    };

    // Detect the role of each option based on value patterns (language-independent)
    this.detectOptionRoles = (options, productOptions) => {
      const roles = {};

      options.forEach((option, index) => {
        const optionName = (productOptions?.[index]?.name || "").toLowerCase();
        const optionValue = option.toLowerCase();

        // Size detection: look for dimension patterns
        if (/\d+\s*[x×xX]\s*\d+/.test(option)) {
          roles[index] = "size";
        }
        // Frame detection: enhanced synonyms including translations
        else if (this.isFrameOption(optionValue, optionName)) {
          roles[index] = "frame";
        }
        // Product type detection: remaining option or explicit type indicators
        else if (this.isProductTypeOption(optionValue, optionName)) {
          roles[index] = "productType";
        }
        // Fallback: use option name patterns
        else if (
          optionName.includes("storlek") ||
          optionName.includes("size") ||
          optionName.includes("taille") ||
          optionName.includes("größe") ||
          optionName.includes("dimensioner") ||
          optionName.includes("dimensions")
        ) {
          roles[index] = "size";
        } else if (
          optionName.includes("ram") ||
          optionName.includes("frame") ||
          optionName.includes("cadre") ||
          optionName.includes("rahmen") ||
          optionName.includes("marco")
        ) {
          roles[index] = "frame";
        } else if (
          optionName.includes("produkttyp") ||
          optionName.includes("product type") ||
          optionName.includes("type de produit") ||
          optionName.includes("produktart") ||
          optionName.includes("tipo de producto")
        ) {
          roles[index] = "productType";
        }
      });

      return roles;
    };

    // Check if option value indicates frame type
    this.isFrameOption = (optionValue, optionName) => {
      const frameIndicators = [
        "frame",
        "ram",
        "cadre",
        "rahmen",
        "marco",
        "white",
        "vit",
        "weiß",
        "weiss",
        "blanc",
        "blanco",
        "black",
        "svart",
        "schwarz",
        "noir",
        "negro",
        "wood",
        "wooden",
        "natural",
        "naturlig",
        "natur",
        "holz",
        "bois",
        "madera",
        "dark",
        "mörk",
        "dunkel",
        "sombre",
        "oscuro",
        "none",
        "ingen",
        "utan",
        "kein",
        "sans",
        "sin",
      ];

      return frameIndicators.some((indicator) => optionValue.includes(indicator) || optionName.includes(indicator));
    };

    // Check if option value indicates product type
    this.isProductTypeOption = (optionValue, optionName) => {
      const typeIndicators = ["canvas", "duk", "poster", "affisch", "leinwand", "toile"];

      return typeIndicators.some((indicator) => optionValue.includes(indicator) || optionName.includes(indicator));
    };

    // Normalize frame names to internal keys with extended language support
    this.normalizeFrameName = (frameName) => {
      if (!frameName || typeof frameName !== "string") return "none";

      const frame = frameName.toLowerCase().trim();

      // No frame variations (en/sv/de/fr/es)
      if (
        frame.includes("no frame") ||
        frame.includes("utan ram") ||
        frame.includes("ingen ram") ||
        frame.includes("kein rahmen") ||
        frame.includes("sans cadre") ||
        frame.includes("sin marco") ||
        frame.includes("none") ||
        frame.includes("no") ||
        frame.includes("nej")
      ) {
        return "none";
      }

      // White frame variations
      if (
        frame.includes("white") ||
        frame.includes("vit") ||
        frame.includes("weiß") ||
        frame.includes("weiss") ||
        frame.includes("blanc") ||
        frame.includes("blanco")
      ) {
        return "white";
      }

      // Natural/Wood frame variations (including "wooden frame")
      if (
        frame.includes("wood") ||
        frame.includes("wooden") ||
        frame.includes("natural") ||
        frame.includes("naturlig") ||
        frame.includes("natur") ||
        frame.includes("holz") ||
        frame.includes("bois") ||
        frame.includes("madera")
      ) {
        return "natural";
      }

      // Dark wood frame variations
      if (
        frame.includes("dark wood") ||
        frame.includes("dark") ||
        frame.includes("mörk") ||
        frame.includes("dunkel") ||
        frame.includes("sombre") ||
        frame.includes("oscuro")
      ) {
        return "dark";
      }

      // Black frame variations
      if (
        frame.includes("black") ||
        frame.includes("svart") ||
        frame.includes("schwarz") ||
        frame.includes("noir") ||
        frame.includes("negro")
      ) {
        return "black";
      }

      return "with-frame"; // Default fallback
    };

    // Helper method for variant-picker handling

    this.getSelectedOptionsFromPicker = () => {
      const options = {};

      // Använd fieldset[data-option-index] för korrekt ordning
      const fieldsets = document.querySelectorAll("variant-picker fieldset[data-option-index]");

      fieldsets.forEach((fieldset) => {
        const optionIndex = fieldset.getAttribute("data-option-index"); // "option1", "option2", etc.
        const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
        const selectInput = fieldset.querySelector("select");

        const value = checkedInput?.value || selectInput?.value;
        if (value && optionIndex) {
          options[optionIndex] = value.trim();
        }
      });

      // Fallback för temat som inte använder fieldset[data-option-index]
      if (Object.keys(options).length === 0) {
        const checkedInputs = document.querySelectorAll('variant-picker input[type="radio"]:checked');
        checkedInputs.forEach((input, index) => {
          const value = input.value || input.getAttribute("data-option-value");
          if (value) {
            options[`option${index + 1}`] = value.trim();
          }
        });

        const selectInputs = document.querySelectorAll("variant-picker select");
        selectInputs.forEach((select, index) => {
          if (select.value) {
            const optionIndex = checkedInputs.length + index + 1;
            options[`option${optionIndex}`] = select.value.trim();
          }
        });
      }

      return options;
    };

    attachVariantInputListener();

    // Kapa riktiga submiten om customizer är aktiv
    const hookFormSubmit = () => {
      const form = document.querySelector('form[action*="/cart/add"]');
      if (form && !form.dataset.customizerSubmitHook) {
        form.addEventListener("submit", (e) => {
          if (this.hasActiveDesign && this.hasActiveDesign()) {
            e.preventDefault();
            const formData = new FormData(form);
            // trigga vår pipeline
            this.handleAddToCart({ formData });
          }
        });
        form.dataset.customizerSubmitHook = "true";
      }
    };
    hookFormSubmit();

    // …och kör igen när DOM uppdateras (teman byter ofta ut formuläret)
    const fo = new MutationObserver(hookFormSubmit);
    fo.observe(document.body, { childList: true, subtree: true });

    // Listen for customizer updates
    document.addEventListener("customizer:update", (event) => {
      this.handleCustomizerUpdate?.(event.detail);
    });

    // Listen for layer changes and selections
    document.addEventListener("customizer:layers:change", (event) => {
      console.debug("[CustomizerBridge] Received customizer:layers:change (native):", event.detail);
      this.updateLayerList(event.detail);
    });

    // Also listen for layers:change events (native embed mode)
    document.addEventListener("layers:change", (event) => {
      console.debug("[CustomizerBridge] Received layers:change (native):", event.detail);
      this.updateLayerList(event.detail);
    });

    document.addEventListener("customizer:layer:selected", (event) => {
      const detail = event.detail;
      const selectedId =
        detail && typeof detail === "object"
          ? (detail.layer && detail.layer.id) || detail.layerId || detail.id || null
          : detail;
      this.updateLayerSelection(selectedId);
    });

    // Also listen for layer:selected events (from CanvasEditor)
    document.addEventListener("layer:selected", (event) => {
      console.debug("[CustomizerBridge] Received layer:selected (native):", event.detail);
      this.updateLayerSelection(event.detail.layerId);
    });

    // Listen for add to cart
    document.addEventListener("product:add-to-cart", (event) => {
      this.handleAddToCart(event.detail);
    });

    // Add method to check if customizer has active design
    this.hasActiveDesign = () => {
      try {
        const data = window.CustomizerNativeLoader?.getDesignData?.();
        const hasMotifs = !!(data?.motifs && data.motifs.some((m) => m.layers?.length));
        const hasCanvas = !!document.querySelector(
          '[data-block-type="customizer-canvas"], .customizer-canvas-wrapper, .customizer-canvas-native',
        );

        console.debug("[Bridge] hasActiveDesign check:", {
          hasNativeLoader: !!window.CustomizerNativeLoader,
          hasDesignData: !!data,
          motifCount: data?.motifs?.length || 0,
          hasMotifs,
          hasCanvas,
          result: hasMotifs || hasCanvas,
        });

        return hasMotifs || hasCanvas;
      } catch (_) {
        return !!document.querySelector(
          '[data-block-type="customizer-canvas"], .customizer-canvas-wrapper, .customizer-canvas-native',
        );
      }
    };

    // Listen for ADD_TO_CART messages from React components
    window.addEventListener("message", (event) => {
      if (event.data?.type === "ADD_TO_CART") {
        console.debug("[Bridge] Received ADD_TO_CART message:", event.data);
        this.handleDirectCartAdd(event.data);
        return;
      }

      if (event.data?.type === "SET_CART_EXPECTATION") {
        console.debug("[Bridge] Set cart expectation:", event.data.count);
        this.expectedCartItems = event.data.count || 1;
        return;
      }
    });

    // Listen for postMessage from variant picker and other sources
    window.addEventListener("message", (event) => {
      console.debug("[CustomizerBridge] Received postMessage:", event.data, "from origin:", event.origin);

      if (event.data.type === "customizer:resize") {
        this.handleCustomizerResize(event.data);
      }

      // Handle layer selection messages from React customizer
      if (event.data.type === "layer:selected") {
        console.debug("[CustomizerBridge] Processing layer:selected from iframe:", event.data);
        this.handleLayerSelection(event.data);
      }

      // Handle layers change messages from React customizer (iframe mode)
      if (event.data.type === "layers:change") {
        console.debug("[CustomizerBridge] Processing layers:change from iframe:", event.data);
        this.updateLayerList(event.data);
      }

      // Handle layer selection messages from React customizer (iframe mode)
      if (event.data.type === "layer:selected") {
        console.debug("[CustomizerBridge] Processing layer:selected from iframe:", event.data);
        const selectedId = (event.data.layer && event.data.layer.id) || event.data.layerId || null;
        this.updateLayerSelection(selectedId);
      }

      // Handle variant picker messages
      if (event.data.type === "variant:change") {
        console.debug("[CustomizerBridge] Processing variant:change from postMessage");
        this.handleVariantChange(event.data, false); // Don't dispatch DOM event
      }
    });

    // Listen for native embed resize events
    document.addEventListener("customizer:resize", (event) => {
      this.handleCustomizerResize(event.detail);
    });

    // Listen for customizer variant change events (from variant picker)
    document.addEventListener("customizer:variant-change", (event) => {
      console.debug("[CustomizerBridge] Processing customizer:variant-change event");
      // this.handleVariantChange(event.detail, false); // ❌
      this.handleCustomizerVariantChange(event.detail); // ✅
    });

    // DOM content loaded
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => {
        this.initializeBlocks();
        this.setupVariantWatchers();
      });
    } else {
      this.initializeBlocks();
      this.setupVariantWatchers();
    }

    // Re-setup variant watchers periodically to catch dynamically loaded selectors
    this.setupVariantWatchers();
  }

  setupVariantWatchers() {
    // Enhanced method to setup variant watchers
    const attachVariantInputListener = () => {
      // Listen to the main variant input
      const input = document.querySelector('form[action*="/cart/add"] [name="id"]');
      if (input && !input.dataset.customizerListener) {
        input.addEventListener("change", () => {
          const variantId = input.value;
          this.handleVariantInputChange(variantId);
        });
        input.dataset.customizerListener = "true";
      }

      // Listen to variant-picker inputs (Shopify's native variant selector)
      const variantPickerInputs = document.querySelectorAll(
        'variant-picker input[type="radio"], variant-picker select',
      );
      variantPickerInputs.forEach((input) => {
        if (!input.dataset.customizerListener) {
          input.addEventListener("change", () => {
            console.debug("[CustomizerBridge] Variant picker input changed:", input.name, input.value);
            this.handleVariantPickerChange();
          });
          input.dataset.customizerListener = "true";
        }
      });

      // Listen to variant selector dropdowns and buttons (legacy/fallback)
      const variantSelectors = document.querySelectorAll('[name^="options"], .variant-input, [data-variant-input]');
      variantSelectors.forEach((selector) => {
        if (!selector.dataset.customizerListener) {
          selector.addEventListener("change", () => {
            setTimeout(() => {
              const currentVariantId = document.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
              if (currentVariantId) {
                this.handleVariantInputChange(currentVariantId);
              }
            }, 100);
          });
          selector.dataset.customizerListener = "true";
        }
      });

      // Listen to variant buttons
      const variantButtons = document.querySelectorAll("[data-variant-id], .variant-button, .swatch");
      variantButtons.forEach((button) => {
        if (!button.dataset.customizerListener) {
          button.addEventListener("click", () => {
            setTimeout(() => {
              const currentVariantId = document.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
              if (currentVariantId) {
                this.handleVariantInputChange(currentVariantId);
              }
            }, 200);
          });
          button.dataset.customizerListener = "true";
        }
      });
    };

    attachVariantInputListener();
  }

  // Initialize layer quick action buttons
  initLayerQuickActions() {
    console.debug("[CustomizerBridge] Initializing layer quick actions");
    
    // Wait for DOM to be ready
    const setupButtons = () => {
      const rotateBtn = document.getElementById('layer-rotate-90');
      const centerHBtn = document.getElementById('layer-center-h');
      const centerVBtn = document.getElementById('layer-center-v');
      
      if (!rotateBtn || !centerHBtn || !centerVBtn) {
        console.debug("[CustomizerBridge] Quick action buttons not found yet, will retry");
        return false;
      }
      
      // Add click handlers
      rotateBtn.addEventListener('click', () => {
        if (!this.currentSelectedLayerId) return;
        console.debug("[CustomizerBridge] Rotate 90° clicked for layer:", this.currentSelectedLayerId);
        this.notifyBlocks('layer:rotate-90', { layerId: this.currentSelectedLayerId });
      });
      
      centerHBtn.addEventListener('click', () => {
        if (!this.currentSelectedLayerId) return;
        console.debug("[CustomizerBridge] Center horizontal clicked for layer:", this.currentSelectedLayerId);
        this.notifyBlocks('layer:center-horizontal', { layerId: this.currentSelectedLayerId });
      });
      
      centerVBtn.addEventListener('click', () => {
        if (!this.currentSelectedLayerId) return;
        console.debug("[CustomizerBridge] Center vertical clicked for layer:", this.currentSelectedLayerId);
        this.notifyBlocks('layer:center-vertical', { layerId: this.currentSelectedLayerId });
      });
      
      console.debug("[CustomizerBridge] Layer quick action buttons initialized");
      return true;
    };
    
    // Try immediately, or wait for DOM
    if (!setupButtons()) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setupButtons);
      } else {
        // Retry after a short delay (buttons might be added dynamically)
        setTimeout(setupButtons, 500);
      }
    }
  }

  // Update quick action button states based on selection
  updateQuickActionButtons() {
    const rotateBtn = document.getElementById('layer-rotate-90');
    const centerHBtn = document.getElementById('layer-center-h');
    const centerVBtn = document.getElementById('layer-center-v');
    
    const hasSelection = !!this.currentSelectedLayerId;
    
    if (rotateBtn) rotateBtn.disabled = !hasSelection;
    if (centerHBtn) centerHBtn.disabled = !hasSelection;
    if (centerVBtn) centerVBtn.disabled = !hasSelection;
    
    console.debug("[CustomizerBridge] Updated quick action buttons:", { 
      hasSelection, 
      layerId: this.currentSelectedLayerId 
    });
  }

  loadProductData() {
    // Prefer data attributes from any element that carries product data
    const canvasElement = document.querySelector("[data-product-id]");
    if (canvasElement) {
      this.productId = canvasElement.dataset.productId || this.productId;
      this.selectedVariantId = canvasElement.dataset.selectedVariant || this.selectedVariantId;
      this.shopDomain = canvasElement.dataset.shopDomain || this.shopDomain;
    }

    // Fallback: parse product JSON from theme using robust reading
    if (!this.productId || !this.selectedVariantId) {
      // Use same robust JSON reading logic as handleVariantInputChange
      let productJsonEl = null;

      // Priority 1: Try inline product JSON first
      const inlineJson = document.querySelector("#product-json-inline[data-product-json]");
      if (inlineJson && inlineJson.textContent.trim()) {
        productJsonEl = inlineJson;
      }

      // Priority 2: Try global product JSON
      if (!productJsonEl || !productJsonEl.textContent.trim()) {
        const globalJson = document.querySelector("#product-json[data-product-json]");
        if (globalJson && globalJson.textContent.trim()) {
          productJsonEl = globalJson;
        }
      }

      // Priority 3: Try any product JSON element with content
      if (!productJsonEl || !productJsonEl.textContent.trim()) {
        const allJsonElements = document.querySelectorAll("[data-product-json]");
        for (const el of allJsonElements) {
          if (el.textContent && el.textContent.trim().startsWith("{")) {
            productJsonEl = el;
            break;
          }
        }
      }

      try {
        if (productJsonEl?.textContent) {
          const product = JSON.parse(productJsonEl.textContent);
          this.productId = this.productId || String(product.id || "");
          if (!this.selectedVariantId && Array.isArray(product.variants) && product.variants.length) {
            const selectedInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
            this.selectedVariantId = selectedInput?.value || String(product.variants[0].id || "");
          }
        }
      } catch (e) {
        console.debug("[CustomizerBridge] Error parsing product JSON", e);
      }
    }

    // Fallback shop domain
    this.shopDomain = this.shopDomain || (window.Shopify && window.Shopify.shop) || location.hostname;

    // Load variant price mapping
    this.loadVariantPrices();
  }

  loadVariantPrices() {
    // Use same robust JSON reading as other methods
    let productJsonEl = null;

    // Priority 1: Try inline product JSON first
    const inlineJson = document.querySelector("#product-json-inline[data-product-json]");
    if (inlineJson && inlineJson.textContent.trim()) {
      productJsonEl = inlineJson;
    }

    // Priority 2: Try global product JSON
    if (!productJsonEl || !productJsonEl.textContent.trim()) {
      const globalJson = document.querySelector("#product-json[data-product-json]");
      if (globalJson && globalJson.textContent.trim()) {
        productJsonEl = globalJson;
      }
    }

    if (productJsonEl) {
      try {
        const product = JSON.parse(productJsonEl.textContent);
        if (product && Array.isArray(product.variants)) {
          product.variants.forEach((variant) => {
            this.variantPriceMap.set(variant.id.toString(), {
              price: variant.price,
              compare_at_price: variant.compare_at_price,
              available: variant.available,
            });
          });
        }
      } catch (error) {
        console.debug("Error parsing product JSON for prices:", error);
      }
    }
  }

  getProductInfoContainer() {
    const selectors = [
      ".product__info-container",
      ".product__info",
      "[data-product-info]",
      '[id^="ProductInfo-"]',
      "#ProductInfo",
      ".product__details",
      ".product-single__meta",
      ".product__content",
      ".product--info",
      ".product__right", // common pattern
    ];
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el) return el;
    }
    // Fallback to nearest sibling/parent of product form
    const form = document.querySelector('form[action*="/cart/add"]');
    return form?.parentElement || document.body;
  }

  ensureCustomizerCanvas() {
    // Only return existing canvas, don't inject new one
    return this.findCanvasEl();
  }

  findCanvasEl() {
    // Robust canvas element finding with multiple selector patterns
    const selectors = [
      '[data-block-type="customizer-canvas"]',
      '[id^="customizer-canvas-"]',
      '.customizer-canvas-wrapper [data-block-type="customizer-canvas"]',
      '[data-customizer-slot] [data-block-type="customizer-canvas"]',
    ];

    for (const selector of selectors) {
      const el = document.querySelector(selector);
      if (el) return el;
    }
    return null;
  }

  getGalleryContainer() {
    // Find gallery container in order of preference
    const selectors = [
      ".product__gallery-container",
      ".product__media-wrapper",
      "[data-media-gallery-wrapper]",
      ".product__media-list",
      ".product__gallery",
      ".product-gallery",
    ];

    for (const selector of selectors) {
      const container = document.querySelector(selector);
      if (container) return container;
    }

    // Fallback: find parent of first gallery element
    const firstGalleryEl = this.findFirstGalleryEl();
    return firstGalleryEl?.parentElement || null;
  }

  findFirstGalleryEl() {
    const selectors = [
      "[data-media-gallery]",
      ".product__media",
      ".product__gallery",
      "#MediaGallery",
      "media-gallery",
      ".product-gallery",
      "[data-product-gallery]",
    ];

    for (const selector of selectors) {
      const el = document.querySelector(selector);
      if (el) return el;
    }
    return null;
  }

  initializeBlocks() {
    const canvasBlock = this.findCanvasEl();

    if (canvasBlock) {
      // Canvas found immediately, proceed with boot
      this.bootWithCanvas(canvasBlock);
      return;
    }

    // Canvas not found, use MutationObserver to wait for it
    console.debug("[CustomizerBridge] Canvas not found immediately, waiting for it to appear...");

    const observer = new MutationObserver((mutations) => {
      const foundCanvas = this.findCanvasEl();
      if (foundCanvas) {
        observer.disconnect();
        console.debug("[CustomizerBridge] Canvas appeared, booting...");
        this.bootWithCanvas(foundCanvas);
      }
    });

    // Watch for additions to the DOM
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Also watch for variant selector changes periodically
    const variantWatcher = setInterval(() => {
      this.setupVariantWatchers();
    }, 1000);

    // Timeout after 10 seconds
    setTimeout(() => {
      observer.disconnect();
      clearInterval(variantWatcher);
      const finalCheck = this.findCanvasEl();
      if (!finalCheck) {
        console.debug("[CustomizerBridge] Canvas element never appeared, skipping customizer initialization");
      }
    }, 10000);
  }

  bootWithCanvas(canvasBlock) {
    console.debug("[CustomizerBridge] bootWithCanvas", { canvasBlock });

    // Find all customizer-related blocks
    const blockElements = document.querySelectorAll("[data-block-type]");
    blockElements.forEach((element) => {
      const blockType = element.dataset.blockType;
      const blockId = element.id;
      this.blocks.set(blockId, {
        type: blockType,
        element: element,
        loaded: false,
      });
    });

    console.debug("[CustomizerBridge] bootWithCanvas complete", {
      canvasFound: !!canvasBlock,
      blockCount: blockElements.length,
      productId: this.productId,
      shopDomain: this.shopDomain,
    });

    // Set up gallery sync and load components
    this.setupGallerySync(true);
    this.loadReactComponents();
  }

  replaceProductGallery() {
    try {
      const customizerCanvas = this.findCanvasEl();
      if (!customizerCanvas) return;

      const galleryContainer = this.getGalleryContainer();
      if (!galleryContainer) {
        console.debug("[CustomizerBridge] No gallery container found");
        return;
      }

      // Hide all gallery elements within the container
      const selectors = [
        "[data-media-gallery]",
        ".product__media",
        ".product__media-list",
        ".product__gallery",
        "#MediaGallery",
        "media-gallery",
        ".product-gallery",
        "[data-product-gallery]",
      ];

      const galleryEls = selectors.flatMap((sel) => Array.from(galleryContainer.querySelectorAll(sel)));

      console.debug("[CustomizerBridge] replaceProductGallery", {
        galleryContainer,
        galleryCount: galleryEls.length,
        canvasCurrentParent: customizerCanvas.parentElement?.className,
      });

      // Hide gallery elements
      galleryEls.forEach((el) => {
        try {
          el.style.display = "none";
          el.setAttribute("data-customizer-hidden", "true");
        } catch (_) {}
      });

      // Move customizer canvas into gallery container if not already there
      if (!galleryContainer.contains(customizerCanvas)) {
        // Create or find slot for customizer within gallery container
        let slot = galleryContainer.querySelector("[data-customizer-slot]");
        if (!slot) {
          slot = document.createElement("div");
          slot.setAttribute("data-customizer-slot", "true");
          slot.style.width = "100%";
          galleryContainer.insertBefore(slot, galleryContainer.firstChild);
        }
        slot.appendChild(customizerCanvas);
      }

      // Set active attributes
      galleryContainer.setAttribute("data-customizer-active", "true");
      document.body.setAttribute("data-customizer-active", "true");

      // Style customizer canvas
      customizerCanvas.style.display = "block";
      customizerCanvas.style.width = "100%";
      customizerCanvas.style.minHeight = "400px";

      this.galleryReplaced = true;

      // Clear timeout if iframe loads
      const onIframeLoad = () => {
        if (this.galleryRestoreTimer) {
          clearTimeout(this.galleryRestoreTimer);
          this.galleryRestoreTimer = null;
          console.debug("[CustomizerBridge] Iframe loaded, timeout cleared");
        }
      };

      const attachLoadListener = (iframeEl) => {
        try {
          iframeEl.addEventListener("load", onIframeLoad, { once: true });
        } catch (_) {}
      };

      // Watch for iframe creation and attach load listener
      const existingIframe = customizerCanvas.querySelector("iframe");
      if (existingIframe) {
        attachLoadListener(existingIframe);
      } else {
        const mo = new MutationObserver(() => {
          const iframe = customizerCanvas.querySelector("iframe");
          if (iframe) {
            attachLoadListener(iframe);
            mo.disconnect();
          }
        });
        mo.observe(customizerCanvas, { childList: true, subtree: true });
      }
    } catch (e) {
      console.debug("[CustomizerBridge] replaceProductGallery error", e);
    }
  }

  restoreProductGallery() {
    try {
      const customizerCanvas = this.findCanvasEl();
      const customizerSlot = document.querySelector("[data-customizer-slot]");

      if (this.galleryRestoreTimer) {
        clearTimeout(this.galleryRestoreTimer);
        this.galleryRestoreTimer = null;
      }

      // Remove active attributes
      const container = document.querySelector('[data-customizer-active="true"]');
      if (container) {
        container.removeAttribute("data-customizer-active");
      }
      document.body.removeAttribute("data-customizer-active");

      // Show previously hidden gallery elements
      const previouslyHidden = document.querySelectorAll("[data-customizer-hidden]");
      previouslyHidden.forEach((el) => {
        try {
          el.style.display = "";
          el.removeAttribute("data-customizer-hidden");
        } catch (_) {}
      });

      // Move canvas back to its original wrapper if it exists
      if (customizerCanvas && customizerSlot) {
        const originalWrapper = document.querySelector(".customizer-canvas-wrapper");
        if (originalWrapper && !originalWrapper.contains(customizerCanvas)) {
          originalWrapper.appendChild(customizerCanvas);
        }
      }

      // Clean up customizer slot if empty
      if (customizerSlot && !customizerSlot.children.length) {
        customizerSlot.remove();
      }

      this.galleryReplaced = false;
      console.debug("[CustomizerBridge] restoreProductGallery: done");
    } catch (e) {
      console.debug("[CustomizerBridge] restoreProductGallery error", e);
    }
  }

  setupGallerySync(activateNow) {
    try {
      if (activateNow) {
        this.replaceProductGallery();
      } else {
        this.restoreProductGallery();
      }

      if (this.galleryObserver) {
        this.galleryObserver.disconnect();
      }

      this.galleryObserver = new MutationObserver(() => {
        const canvas = this.findCanvasEl();

        if (canvas && !this.galleryReplaced) {
          this.replaceProductGallery();
        }

        if ((!canvas || !document.body.contains(canvas)) && this.galleryReplaced) {
          this.restoreProductGallery();
        }
      });

      this.galleryObserver.observe(document.body, { childList: true, subtree: true });
    } catch (e) {
      console.debug("[CustomizerBridge] setupGallerySync error", e);
    }
  }

  async loadReactComponents() {
    // Wait for CustomizerNativeLoader and React to be loaded
    const start = Date.now();
    const poll = setInterval(() => {
      if (window.CustomizerNativeLoader && window.CustomizerNativeLoader.reactLoaded) {
        clearInterval(poll);
        this.renderBlocks();
      } else if (Date.now() - start > 8000) {
        clearInterval(poll);
        console.warn("CustomizerNativeLoader or React not loaded after waiting");
      }
    }, 100);
  }

  renderBlocks() {
    this.blocks.forEach((block, blockId) => {
      if (!block.loaded && window.CustomizerNativeLoader) {
        try {
          window.CustomizerNativeLoader.renderBlock(blockId, block.type, {
            productId: this.productId,
            selectedVariantId: this.selectedVariantId,
            shopDomain: this.shopDomain,
            onUpdate: (data) => this.handleBlockUpdate(blockId, data),
          });
          block.loaded = true;
        } catch (error) {
          console.error(`Error rendering block ${blockId}:`, error);
        }
      }
    });
  }

  handleVariantChange(variantData, dispatchEvent = true) {
    // Prevent infinite loops with reentrancy guard
    if (this.isHandlingVariantChange) {
      console.debug("[CustomizerBridge] Already handling variant change, skipping");
      return;
    }

    this.isHandlingVariantChange = true;

    try {
      const { variant, productType, size, frame, orientation } = variantData;
      this.selectedVariantId = variant?.id || this.selectedVariantId;

      console.debug("[CustomizerBridge] Processing variant change:", {
        variantId: variant?.id,
        productType,
        size,
        frame,
        orientation,
        dispatchEvent,
        variantData,
      });

      // Handle missing variant data - fallback to script or form data
      let workingVariant = variant;
      if (!workingVariant || !workingVariant.options) {
        // Try to get variant from selected-variant-json script
        const variantScript = document.querySelector("#selected-variant-json, script[data-variant]");
        if (variantScript) {
          try {
            const scriptVariant = JSON.parse(variantScript.textContent);
            if (scriptVariant && scriptVariant.options) {
              workingVariant = scriptVariant;
              console.debug("[Bridge] Using fallback variant from script");
            }
          } catch (e) {
            console.debug("[Bridge] Could not parse variant script");
          }
        }

        // Final fallback: create minimal variant with form data
        if (!workingVariant || !workingVariant.options) {
          const cartForm = document.querySelector('form[action*="/cart/add"]');
          const variantInput = cartForm?.querySelector('[name="id"]');
          const variantId = variantInput?.value || this.selectedVariantId;
          workingVariant = {
            id: Number(variantId),
            options: [],
            price: variantData?.price || this.getVariantPrice(variantId) || 0,
          };
          console.debug("[Bridge] Using minimal fallback variant");
        }
      }

      // Map variant to customizer size with defensive approach
      const mappedSize = size || this.mapVariantToSize(workingVariant);

      // Update customizer with new size
      this.updateCustomizerState({
        selectedVariantId: this.selectedVariantId,
        size: mappedSize,
        price: variant?.price ?? variantData?.price ?? this.getVariantPrice(this.selectedVariantId) ?? 0,
      });

      // Update script elements to prevent JSON parsing errors in theme.js
      if (variant?.id) {
        this.updateSelectedVariantScript(variant.id);
      }

      // Only trigger variant:change event if requested (prevents infinite loops)
      if (dispatchEvent) {
        console.debug("[CustomizerBridge] Triggering variant:change event for theme.js");
        const variantChangeEvent = new CustomEvent("variant:change", {
          detail: {
            variant: variant,
            productType,
            size: mappedSize,
            frame,
            orientation,
          },
        });
        document.dispatchEvent(variantChangeEvent);
      }

      // Send messages to iframe about variant changes (for iframe-based customizers)
      const iframe = this.getCustomizerIframe();
      if (iframe && iframe.contentWindow) {
        console.debug("[CustomizerBridge] Sending postMessage to iframe");

        // Send variant change
        iframe.contentWindow.postMessage(
          {
            type: "variant:change",
            variantId: this.selectedVariantId,
            size: mappedSize,
          },
          "*",
        );

        // Send product type change if available
        if (productType) {
          iframe.contentWindow.postMessage(
            {
              type: "product:type-change",
              productType: productType,
            },
            "*",
          );
        }

        // Send orientation change if available
        if (orientation) {
          iframe.contentWindow.postMessage(
            {
              type: "product:orientation-change",
              orientation: orientation,
            },
            "*",
          );
        }

        // Send frame change if available
        if (frame) {
          iframe.contentWindow.postMessage(
            {
              type: "product:frame-change",
              frame: frame,
            },
            "*",
          );
        }
      }

      // Trigger block-specific variant change events for native blocks
      console.debug("[CustomizerBridge] Triggering block:variant:change event for native blocks");
      const blockVariantEvent = new CustomEvent("block:variant:change", {
        detail: {
          variantId: this.selectedVariantId,
          size: mappedSize,
          productType,
          frame,
          orientation,
        },
      });
      document.dispatchEvent(blockVariantEvent);

      // Notify all blocks about variant change
      this.blocks.forEach((block, blockId) => {
        this.notifyBlock(blockId, "variant:change", {
          variantId: this.selectedVariantId,
          size: mappedSize,
          productType,
          frame,
          orientation,
        });
      });
    } finally {
      // Reset reentrancy guard
      this.isHandlingVariantChange = false;
    }
  }

  getCustomizerIframe() {
    // Try multiple selectors to find the iframe robustly
    const selectors = [
      `#customizer-canvas-${this.productId} iframe`,
      '[data-block-type="customizer-canvas"] iframe',
      ".customizer-canvas-wrapper iframe",
      ".customizer-canvas-native iframe",
      ".supabase-embed-container iframe",
      ".customizer-canvas-container iframe",
      "[data-product-id] iframe",
    ];

    for (const selector of selectors) {
      const iframe = document.querySelector(selector);
      if (iframe && iframe.contentWindow) {
        console.debug("[CustomizerBridge] Found iframe with selector:", selector);
        return iframe;
      }
    }

    console.debug("[CustomizerBridge] No iframe found - likely using native block approach");
    return null;
  }

  mapVariantToSize(variant) {
    try {
      const sizeMapping = {
        "13x18": { width: 13, height: 18, label: "13x18 cm" },
        "21x30": { width: 21, height: 30, label: "21x30 cm" },
        "30x40": { width: 30, height: 40, label: "30x40 cm" },
        "50x70": { width: 50, height: 70, label: "50x70 cm" },
      };

      // Handle missing variant or options
      if (!variant || !variant.options || !Array.isArray(variant.options)) {
        console.debug("[Bridge] mapVariantToSize: No valid variant options, using default");
        return { width: 30, height: 40, label: "30x40 cm" };
      }

      // Normalize size matching with regex to handle "NN x MM cm", "NNxMM", etc.
      const sizeRegex = /(\d+)\s*x\s*(\d+)/i;

      const sizeOption = variant.options.find((opt) => {
        if (!opt || typeof opt !== "string") return false;
        const match = opt.match(sizeRegex);
        if (match) {
          const sizeKey = `${match[1]}x${match[2]}`;
          return Object.keys(sizeMapping).includes(sizeKey);
        }
        return false;
      });

      if (sizeOption) {
        const match = sizeOption.match(sizeRegex);
        if (match) {
          const sizeKey = `${match[1]}x${match[2]}`;
          const mappedSize = sizeMapping[sizeKey];
          if (mappedSize) {
            console.debug("[Bridge] mapVariantToSize: Found size mapping", { option: sizeOption, mapped: mappedSize });
            return mappedSize;
          }
        }
      }

      console.debug("[Bridge] mapVariantToSize: No size match found, using default", { variant });
      return { width: 30, height: 40, label: "30x40 cm" };
    } catch (error) {
      console.error("[Bridge] mapVariantToSize error:", error);
      return { width: 30, height: 40, label: "30x40 cm" };
    }
  }

  updateCustomizerState(data) {
    if (window.CustomizerNativeLoader && window.CustomizerNativeLoader.updateState) {
      window.CustomizerNativeLoader.updateState(data);
    }
  }

  notifyBlock(blockId, eventType, data) {
    const block = this.blocks.get(blockId);
    if (block && block.loaded) {
      const event = new CustomEvent(`block:${eventType}`, {
        detail: { blockId, ...data },
      });
      block.element.dispatchEvent(event);
    }
  }

  handleBlockUpdate(blockId, data) {
    console.log(`Block ${blockId} updated:`, data);
    if (data.type === "size-change") {
      this.updateShopifyVariant(data.size);
    }
  }

  updateShopifyVariant(size) {
    const variant = this.findVariantBySize(size);
    if (!variant) {
      console.warn("No matching variant found for size:", size);
      return;
    }

    // Update the hidden selected variant JSON script that theme.js expects
    const selectedVariantScript = document.querySelector("#selected-variant-json[data-selected-variant]");
    if (selectedVariantScript) {
      try {
        selectedVariantScript.textContent = JSON.stringify(variant);
        console.debug("[CustomizerBridge] Updated selected variant JSON script:", variant);
      } catch (e) {
        console.warn("[CustomizerBridge] Error updating selected variant JSON:", e);
      }
    }

    // Trigger the variant change in Shopify
    const variantInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (variantInput && variantInput.value !== String(variant.id)) {
      variantInput.value = variant.id;
      variantInput.dispatchEvent(new Event("change", { bubbles: true }));

      // Also trigger on option inputs if they exist
      const optionInputs = document.querySelectorAll('[name^="options["]');
      if (optionInputs.length > 0) {
        // Find and update the correct option values based on variant
        const productJsonEl = document.querySelector("[data-product-json]");
        if (productJsonEl) {
          try {
            const product = JSON.parse(productJsonEl.textContent);
            const variantData = product.variants?.find((v) => v.id === variant.id);
            if (variantData && variantData.options) {
              variantData.options.forEach((optionValue, index) => {
                const optionInput = document.querySelector(`[name="options[${index + 1}]"]`);
                if (optionInput) {
                  optionInput.value = optionValue;
                  optionInput.dispatchEvent(new Event("change", { bubbles: true }));
                }
              });
            }
          } catch (e) {
            console.warn("Error parsing product JSON:", e);
          }
        }
      }

      console.log("Updated Shopify variant to:", variant);
      this.selectedVariantId = variant.id;
    }
  }

  // Sätter Shopify hidden input + uppdaterar selected-variant-json och triggar theme.js
  selectVariantId(variantId) {
    try {
      const input = document.querySelector('form[action*="/cart/add"] [name="id"]');
      if (input && String(input.value) !== String(variantId)) {
        input.value = String(variantId);
        // uppdatera lagrad state
        this.selectedVariantId = Number(variantId);

        // uppdatera script som vissa teman läser ifrån
        const productJsonEl = document.querySelector(
          "#product-json-inline[data-product-json], #product-json[data-product-json]",
        );
        if (productJsonEl?.textContent?.trim()) {
          try {
            const product = JSON.parse(productJsonEl.textContent);
            const variantObj = product.variants?.find((v) => String(v.id) === String(variantId));
            const selectedVariantScript = document.querySelector(
              'script[type="application/json"][data-selected-variant], #selected-variant-json[data-selected-variant]',
            );
            if (selectedVariantScript && variantObj) {
              selectedVariantScript.textContent = JSON.stringify(variantObj);
            }
          } catch (_) {}
        }

        // trigga vanligt change så tema/sektioner uppdaterar pris/bilder etc
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
    } catch (e) {
      console.debug("[CustomizerBridge] selectVariantId error", e);
    }
  }

  findVariantBySize(sizeArg) {
    // 1) Normalisera indata (till sträng)
    const label = typeof sizeArg === "string" ? sizeArg : sizeArg?.label || "";

    // Plocka ut siffror oberoende av mellanslag/”cm”
    const parseDims = (txt) => {
      if (!txt) return null;
      const m = String(txt)
        .toLowerCase()
        .match(/(\d+)\s*[x×]\s*(\d+)/i);
      if (!m) return null;
      return [parseInt(m[1], 10), parseInt(m[2], 10)];
    };

    const wanted = parseDims(label);
    if (!wanted) {
      console.debug("[Bridge] findVariantBySize: kunde inte tolka size:", sizeArg);
      return null;
    }

    // 2) Läs produktens varianter
    let productJsonEl =
      document.querySelector("#product-json-inline[data-product-json]") ||
      document.querySelector("#product-json[data-product-json]") ||
      document.querySelector("[data-product-json]");
    if (!productJsonEl?.textContent?.trim()) return null;

    try {
      const product = JSON.parse(productJsonEl.textContent);
      if (!product?.variants?.length) return null;

      // 3) Matcha variant där någon option innehåller samma två dimensioner
      const isSameDims = (optTxt) => {
        const dims = parseDims(optTxt);
        if (!dims) return false;
        // matcha oavsett ordning (ifall temat roterar etiketten)
        return (dims[0] === wanted[0] && dims[1] === wanted[1]) || (dims[0] === wanted[1] && dims[1] === wanted[0]);
      };

      const hit = product.variants.find((v) => Array.isArray(v.options) && v.options.some(isSameDims));
      if (hit) {
        console.debug("[Bridge] findVariantBySize matchade:", label, "->", hit.id, hit.options);
        return hit;
      }

      // 4) Fallback: exakt substring (om etiketten råkar vara identisk)
      const hit2 = product.variants.find((v) =>
        v.options?.some((opt) => String(opt).toLowerCase().includes(label.toLowerCase())),
      );
      if (hit2) {
        console.debug("[Bridge] findVariantBySize fallback-substring:", label, "->", hit2.id);
        return hit2;
      }
    } catch (e) {
      console.debug("Error finding variant by size:", e);
    }
    return null;
  }

  // Handle ADD_TO_CART messages directly from React components
  async handleDirectCartAdd(messageData) {
    console.debug("[Bridge] Processing direct ADD_TO_CART:", messageData);

    try {
      const { id: incomingVariantId, quantity = 1, properties = {} } = messageData;
      let variantId = incomingVariantId;

      // Try to resolve a more accurate variant based on current design (size/frame)
      try {
        const designData = window.CustomizerNativeLoader?.getDesignData?.();
        const firstMotif = designData?.motifs?.[0];
        const mapFrameKeyToDisplayName = (frameKey = "none") => {
          const key = String(frameKey).toLowerCase();
          const map = {
            none: "Ingen ram",
            white: "White frame",
            natural: "Wood frame",
            dark: "Dark wood frame",
            black: "Black frame",
          };
          return map[key] || "Ingen ram";
        };
        if (firstMotif?.selectedSize?.label) {
          const selection = {
            productType: firstMotif.productType || designData?.productType || "poster",
            size: firstMotif.selectedSize.label,
            frame: mapFrameKeyToDisplayName(firstMotif.frame),
            orientation: firstMotif.orientation || "portrait",
          };
          const matched = window.CustomizerNativeLoader?.findMatchingVariant?.(selection);
          if (matched?.id) {
            console.debug("[Bridge] Overriding variant for direct add with mapped ID:", matched.id, "for", selection);
            variantId = matched.id;
          }
        }
      } catch (mapErr) {
        console.debug("[Bridge] Variant remap for direct add failed, using incoming id", mapErr);
      }

      if (!variantId) {
        throw new Error("No variant ID provided for ADD_TO_CART");
      }

      // Add to cart using AJAX (this will include the design_id in properties)
      const cartData = {
        id: Number(variantId),
        quantity,
        properties,
      };

      console.debug("[Bridge] Adding to cart via ADD_TO_CART:", cartData);
      const result = await this.ajaxAddToCart(cartData);

      // Notify React component of success
      window.postMessage({ type: "ADD_TO_CART_SUCCESS", result }, "*");

      return result;
    } catch (error) {
      console.error("[Bridge] Direct cart add failed:", error);
      window.postMessage({ type: "ADD_TO_CART_ERROR", error: error.message }, "*");
      throw error;
    }
  }

  async handleAddToCart(cartData) {
    console.log("[CustomizerBridge] Starting add to cart process with:", cartData);

    if (!window.CustomizerNativeLoader) {
      console.error("[CustomizerBridge] CustomizerNativeLoader not found");
      this.fallbackToNormalCart(cartData);
      return;
    }

    const designData = window.CustomizerNativeLoader.getDesignData();

    if (!designData || !designData.motifs || designData.motifs.length === 0) {
      console.warn("[CustomizerBridge] No design data, falling back to normal cart");
      this.fallbackToNormalCart(cartData);
      return;
    }

    // Check for empty motifs
    const hasEmptyMotifs = designData.motifs.some((m) => !m.layers || m.layers.length === 0);
    if (hasEmptyMotifs) {
      alert("Vänligen lägg till innehåll till alla områden innan du lägger till i kundvagnen.");
      return;
    }

    try {
      // Show progress overlay
      this.showProgressOverlay(true, 0, "Förbereder din design...");

      // Step 1: Save design with canvasSize
      this.updateProgress(10, "Sparar design...");

      console.log("💾 Saving design from bridge with canvasSize:", {
        canvasSize: designData.canvasSize,
        motifCount: designData.motifs.length,
      });

      const designId = await this.saveDesign(designData);
      if (!designId) {
        throw new Error("Kunde inte spara design");
      }

      this.updateProgress(20, "Design sparad");

      // Process each motif
      const totalMotifs = designData.motifs.length;
      const progressPerMotif = 70 / totalMotifs;

      for (let motifIndex = 0; motifIndex < designData.motifs.length; motifIndex++) {
        const motif = designData.motifs[motifIndex];
        const currentProgress = 20 + motifIndex * progressPerMotif;

        this.updateProgress(currentProgress, `Bearbetar design ${motifIndex + 1}/${totalMotifs}`);

        // Generate print image
        this.updateProgress(
          currentProgress + progressPerMotif * 0.3,
          `Genererar printbild ${motifIndex + 1}/${totalMotifs}`,
        );

        const printResult = await this.generatePrintImage(designId, motifIndex);
        if (!printResult.success) {
          throw new Error(printResult.error || `Kunde inte generera printbild för design ${motifIndex + 1}`);
        }

        this.updateProgress(
          currentProgress + progressPerMotif * 0.6,
          `Lägger till i kundvagn ${motifIndex + 1}/${totalMotifs}`,
        );

        // Get variant for this motif using frame-aware mapping
        const variantIdEl = document.querySelector('form[action*="/cart/add"] [name="id"]');
        let variantId = variantIdEl?.value || this.selectedVariantId || cartData?.variantId;

        // Map frame key to display name for native loader
        const mapFrameKeyToDisplayName = (frameKey = "none") => {
          const key = String(frameKey).toLowerCase();
          const map = {
            none: "Ingen ram",
            white: "White frame",
            natural: "Wood frame",
            dark: "Dark wood frame",
            black: "Black frame",
          };
          return map[key] || "Ingen ram";
        };

        // Use frame + size for variant selection if motif has size/frame data
        if (motif?.selectedSize) {
          const selection = {
            productType: motif.productType || designData.productType || "poster",
            size: motif.selectedSize?.label,
            frame: mapFrameKeyToDisplayName(motif.frame),
            orientation: motif.orientation || "portrait",
          };
          console.debug("[CustomizerBridge] Trying to map selection:", selection);

          const matched = window.CustomizerNativeLoader?.findMatchingVariant?.(selection);
          if (matched?.id) {
            console.debug("[CustomizerBridge] Found frame-aware variant:", matched.id, "for", selection);
            this.selectVariantId(matched.id);
            variantId = matched.id;
          } else {
            console.warn("[CustomizerBridge] No frame-aware match, falling back to size-only logic");
            const mappedVariant = this.findVariantBySize(motif.selectedSize);
            if (mappedVariant?.id) {
              this.selectVariantId(mappedVariant.id);
              variantId = mappedVariant.id;
            }
          }
        }

        if (!variantId) {
          throw new Error(`Ingen variant hittades för design ${motifIndex + 1}`);
        }

        // Add frame info to properties for debugging
        const extraProps = {};
        if (printResult?.imageUrl) {
          extraProps.print_url = String(printResult.imageUrl);
        }
        if (motif?.frame) {
          extraProps._frame_key = String(motif.frame);
          extraProps._frame_display = mapFrameKeyToDisplayName(motif.frame);
        }

        // 3) Lägg till i varukorgen via nya wrappern (som bygger rätt payload)
        await this.addToCartWithDesign({
          variantId,
          quantity: 1,
          designId,
          motifIndex,
          designData,
          extraProperties: extraProps,
        });

        await this.ensureOrderAttributesFallback({
          designId,
          productUid: designData?.productUid, // om satt
          printUrl: printResult?.imageUrl || "", // från generatePrintImage
        });

        this.updateProgress(currentProgress + progressPerMotif, `Design ${motifIndex + 1} tillagd`);
      }

      this.updateProgress(100, "Alla designer tillagda i kundvagnen!");

      // Hide progress after short delay
      setTimeout(() => {
        this.showProgressOverlay(false);

        // Redirect to cart or show success message
        if (window.location.pathname !== "/cart") {
          window.location.href = "/cart";
        }
      }, 1000);
    } catch (error) {
      console.error("[CustomizerBridge] Error in add to cart:", error);
      this.showProgressOverlay(false);
      alert(`Ett fel uppstod: ${error.message}`);

      // Fallback to normal cart
      this.fallbackToNormalCart(cartData);
    }
  }

  async saveDesign(designData) {
    console.log("🎯 DEBUG: Starting saveDesign with data:", {
      hasDesignData: !!designData,
      productId: this.productId,
    });

    // Get or create user session with robust fallback chain
    let userSession =
      localStorage.getItem("customizer_session") ||
      localStorage.getItem("ai-user-session") ||
      localStorage.getItem("userSession");

    if (!userSession) {
      // Create new session ID and store in both locations
      userSession = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem("customizer_session", userSession);
      localStorage.setItem("ai-user-session", userSession);
      console.log("🆕 DEBUG: Created new user session:", userSession);
    } else {
      console.log("✅ DEBUG: Using existing user session:", userSession);
    }

    const payload = {
      designData: designData,
      userSession: userSession,
      shopifyProductId: this.productId,
      shopDomain: window.location.hostname,
    };

    console.log("📤 DEBUG: Sending saveDesign request:", payload);

    const response = await fetch("https://fevkfaldkdxftapwojfs.supabase.co/functions/v1/save-design", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey:
          "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZldmtmYWxka2R4ZnRhcHdvamZzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTEzOTkxNzcsImV4cCI6MjA2Njk3NTE3N30.n3oswnrS8Xbf82z6T5oOCvMSwkd2i3hYh9Szkc_cIn4",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("❌ DEBUG: Failed to save design:", {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
      });
      throw new Error("Kunde inte spara design");
    }

    const result = await response.json();
    console.log("✅ DEBUG: saveDesign successful, received design_id:", result.designId);
    return result.designId;
  }

  async generatePrintImage(designId, motifIndex) {
    const response = await fetch("https://fevkfaldkdxftapwojfs.supabase.co/functions/v1/generate-print-image", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey:
          "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZldmtmYWxka2R4ZnRhcHdvamZzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTEzOTkxNzcsImV4cCI6MjA2Njk3NTE3N30.n3oswnrS8Xbf82z6T5oOCvMSwkd2i3hYh9Szkc_cIn4",
      },
      body: JSON.stringify({
        designId: designId,
        motifIndex: motifIndex,
        dpi: 300,
      }),
    });

    if (!response.ok) {
      throw new Error("Kunde inte generera printbild");
    }

    return await response.json();
  }

  showProgressOverlay(show, progress = 0, message = "") {
    let overlay = document.getElementById("customizer-progress-overlay");

    if (show && !overlay) {
      overlay = document.createElement("div");
      overlay.id = "customizer-progress-overlay";
      overlay.innerHTML = `
        <div style="
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          color: white;
          font-family: Arial, sans-serif;
        ">
          <div style="
            background: white;
            color: black;
            padding: 2rem;
            border-radius: 8px;
            text-align: center;
            min-width: 300px;
          ">
            <div style="
              width: 48px;
              height: 48px;
              border: 4px solid #f3f3f3;
              border-top: 4px solid #3498db;
              border-radius: 50%;
              animation: spin 1s linear infinite;
              margin: 0 auto 1rem;
            "></div>
            <p id="progress-message" style="margin: 0 0 1rem; font-size: 18px;">Förbereder din design...</p>
            <div style="
              width: 100%;
              height: 8px;
              background: #f3f3f3;
              border-radius: 4px;
              overflow: hidden;
            ">
              <div id="progress-bar" style="
                height: 100%;
                background: #3498db;
                width: 0%;
                transition: width 0.3s ease;
              "></div>
            </div>
            <p id="progress-percent" style="margin: 0.5rem 0 0; font-size: 14px; color: #666;">0%</p>
          </div>
        </div>
        <style>
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        </style>
      `;
      document.body.appendChild(overlay);
    }

    if (overlay) {
      overlay.style.display = show ? "flex" : "none";
      if (show) {
        this.updateProgress(progress, message);
      }
    }

    if (!show && overlay) {
      setTimeout(() => {
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
      }, 100);
    }
  }

  updateProgress(progress, message) {
    const progressBar = document.getElementById("progress-bar");
    const progressMessage = document.getElementById("progress-message");
    const progressPercent = document.getElementById("progress-percent");

    if (progressBar) progressBar.style.width = `${progress}%`;
    if (progressMessage) progressMessage.textContent = message;
    if (progressPercent) progressPercent.textContent = `${Math.round(progress)}%`;
  }

  fallbackToNormalCart(cartData) {
    console.log("⚠️ DEBUG: FALLING BACK TO NORMAL CART - design_id will be lost!");
    console.trace("DEBUG: Fallback triggered from:");
    console.log("[CustomizerBridge] Falling back to normal cart submission");

    // Find the form and submit it normally
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm && cartData.formData) {
      // Create a new form with the original data
      const form = document.createElement("form");
      form.method = "POST";
      form.action = "/cart/add";

      // Add form data
      for (const [key, value] of cartData.formData.entries()) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        form.appendChild(input);
      }

      document.body.appendChild(form);
      console.log("🔄 DEBUG: Submitting normal cart form (no design_id)");
      form.submit();
    } else {
      console.error("❌ DEBUG: Could not find cart form for fallback");
    }
  }

  // === BEGIN CART HELPERS (REPLACES BOTH PREVIOUS addToCartWithDesign METHODS) ===
  async ajaxAddToCart(cartData) {
    console.log("🛒 DEBUG: Adding to cart with data:", {
      cartData,
      hasProperties: !!cartData.properties,
      designId: cartData.properties?.design_id,
    });

    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(cartData),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      console.error("❌ DEBUG: Cart add failed:", {
        status: response.status,
        statusText: response.statusText,
        error: text,
      });
      throw new Error("Failed to add to cart: " + text);
    }

    document.dispatchEvent(new CustomEvent("cart:updated"));
    if (window.theme?.showNotification) {
      window.theme.showNotification("Product added to cart with custom design!");
    }

    const result = await response.json().catch(() => ({}));
    console.log("✅ DEBUG: Cart add successful, response:", result);

    // Log the properties to verify they're preserved
    if (result.items && result.items.length > 0) {
      const addedItem = result.items[result.items.length - 1];
      console.log("🔍 DEBUG: Added item properties:", addedItem.properties);
    }

    return result;
  }

  // Bygger line item + properties för Gelato och kallar ajaxAddToCart
  async addToCartWithDesign({
    variantId,
    quantity = 1,
    designId,
    motifIndex = 0,
    designData,
    extraProperties = {},
    properties = {}, // om du vill skicka in egna properties utöver de nedan
  }) {
    const props = {
      // Gelato / din backend
      design_id: String(designId || ""),
      motif_index: String(motifIndex),
      _customizer: "true",

      // vanliga “hjälpfält” vid behov
      ...(designData?.previewUrl ? { _customizer_preview: String(designData.previewUrl) } : {}),
      ...(designData?.canvasSize?.width ? { _canvas_w: String(designData.canvasSize.width) } : {}),
      ...(designData?.canvasSize?.height ? { _canvas_h: String(designData.canvasSize.height) } : {}),
      ...(designData?.orientation ? { _orientation: String(designData.orientation) } : {}),
      ...(designData?.selectedSize?.label ? { _size_label: String(designData.selectedSize.label) } : {}),

      ...(designData?.productUid ? { _gelato_uid: String(designData.productUid) } : {}),

      // utifrån anropet
      ...properties,
      ...extraProperties,
    };

    // Shopify kräver 'id' (variant-id), inte 'variantId'
    const cartData = {
      id: Number(variantId),
      quantity,
      properties: props,
    };

    console.debug("[CustomizerBridge] ajax payload:", cartData);
    return this.ajaxAddToCart(cartData);
  }
  // === END CART HELPERS ===

  async ensureOrderAttributesFallback({ designId, productUid, printUrl }) {
    try {
      const cart = await fetch("/cart.js")
        .then((r) => r.json())
        .catch(() => null);
      const existing = (cart?.attributes && { ...cart.attributes }) || {};
      const key = "customizer_designs";
      let arr = [];
      try {
        arr = JSON.parse(existing[key] || "[]");
      } catch (_) {
        arr = [];
      }
      arr.push({
        design_id: String(designId || ""),
        gelato_uid: productUid ? String(productUid) : "",
        print_url: printUrl ? String(printUrl) : "",
      });
      const payload = { attributes: { ...existing, [key]: JSON.stringify(arr) } };
      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      console.debug("[Bridge] Wrote fallback note_attributes", payload);
    } catch (e) {
      console.warn("[Bridge] ensureOrderAttributesFallback failed", e);
    }
  }

  handleCustomizerResize(data) {
    if (data.productId && data.height) {
      // Find the customizer iframe and update its height
      const iframe = document.querySelector(`#customizer-canvas-${data.productId} iframe`);
      if (iframe) {
        iframe.style.height = `${data.height}px`;
        console.debug("[CustomizerBridge] Resized iframe to:", data.height + "px");
      }

      // Also update the container height if needed
      const container = document.querySelector(`#customizer-canvas-${data.productId}`);
      if (container) {
        container.style.minHeight = `${data.height}px`;
      }
    }
  }

  // Public API
  getProductId() {
    return this.productId;
  }
  getSelectedVariantId() {
    return this.selectedVariantId;
  }
  getVariantPrice(variantId) {
    const priceData = this.variantPriceMap.get(variantId.toString());
    // Return the price value as number, not the full object
    return typeof priceData === "object" ? priceData.price : priceData;
  }

  // Get product option metadata from variant picker DOM
  getProductOptionsMetaFromPicker() {
    const productOptions = [];
    const variantPicker = document.querySelector("variant-picker");

    if (variantPicker) {
      const fieldsets = variantPicker.querySelectorAll(".variant-input-wrapper[data-option-index]");
      fieldsets.forEach((fieldset, index) => {
        const optionSlug = fieldset.getAttribute("data-option-slug");
        const legend = fieldset.querySelector("legend");
        const labelText = legend ? legend.textContent.trim() : "";

        productOptions.push({
          name: labelText || `Option ${index + 1}`,
          position: index + 1,
          slug: optionSlug,
        });
      });
    }

    return productOptions;
  }

  // Update script elements to prevent JSON parsing errors in theme.js
  updateSelectedVariantScript(variantId) {
    try {
      // Target the correct script element that theme.js reads from
      const scriptElement = document.querySelector('script[type="application/json"][data-selected-variant]');

      if (scriptElement && scriptElement.textContent.trim() === "") {
        // Only update empty script elements with minimal variant data
        const minimalData = {
          id: variantId,
          available: true,
          title: "Custom Design",
          options: ["Poster", "30x40", "Ingen ram"], // Safe defaults
        };
        scriptElement.textContent = JSON.stringify(minimalData);
        console.debug("[CustomizerBridge] Updated empty variant script with minimal data");
      }
    } catch (e) {
      console.debug("[CustomizerBridge] Script update not needed:", e.message);
    }
  }

  handleCustomizerUpdate(detail = {}) {
    // Normalisera multipla payload-varianter
    const size = detail.newSize || detail.size || detail.selectedSize;
    const productType = detail.productType || detail.to;
    const frame = detail.frame;
    const orientation = detail.orientation;

    if (size || productType || frame || orientation) {
      this.handleCustomizerVariantChange({ size, productType, frame, orientation });
    }
  }

  // Handle customizer variant picker changes
  handleCustomizerVariantChange(detail) {
    console.debug("[CustomizerBridge] Building minimal variant for customizer change:", detail);

    // 1) Om customizer skickar en size -> hitta matchande Shopify-variant
    if (detail?.size) {
      const v = this.findVariantBySize(detail.size);
      if (v?.id) {
        console.debug("[CustomizerBridge] Mapping customizer size to Shopify variant id:", v.id);
        this.selectVariantId(v.id); // <-- VIKTIGT
      }
    }

    // 2) Bygg minimal variant för intern state (pris m.m.)
    const cartForm = document.querySelector('form[action*="/cart/add"]');
    const variantInput = cartForm?.querySelector('[name="id"]');
    const variantId = variantInput?.value || this.selectedVariantId;
    const price = detail.price || this.getVariantPrice(variantId) || 0;
    const variant = { id: Number(variantId), price };

    // 3) Uppdatera övrigt state utan att trigga extern DOM-event-loop
    this.handleVariantChange(
      {
        variant,
        productType: detail.productType,
        size: detail.size,
        frame: detail.frame,
        orientation: detail.orientation,
        price,
      },
      false,
    );
  }

  // Handle layer selection messages from React customizer
  handleLayerSelection(messageData) {
    console.debug("[CustomizerBridge] Processing layer selection:", messageData);

    // Extract layer data - handle both old and new formats
    const layerData = messageData.layer || messageData;
    console.debug("[CustomizerBridge] Extracted layer data:", layerData);

    // Always dispatch event, even when layerData is null to reset controls
    const layerSelectedEvent = new CustomEvent("customizer:layer:selected", {
      detail: layerData,
    });
    document.dispatchEvent(layerSelectedEvent);

    // Also send to any registered block targets with wrapped format
    this.sendToTargets("layer:selected", { layer: layerData });
  }

  // Send to targets without DOM event dispatch (prevents loops)
  sendToTargets(type, data) {
    console.debug("[CustomizerBridge] Sending to targets (no DOM event):", type, data);

    // Send postMessage to all possible customizer iframes (robust selectors)
    const iframes = document.querySelectorAll(
      'iframe[src*="customizer"], iframe[src*="customize"], iframe[src*="embed"]',
    );
    iframes.forEach((iframe) => {
      if (iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: type,
            ...data,
          },
          "*",
        );
      }
    });

    // Notify blocks directly without DOM events - correctly iterate the Map
    for (const [blockId, block] of this.blocks) {
      this.notifyBlock(blockId, type, data);
    }
  }

  // Notify all blocks of changes (includes DOM event dispatch)
  notifyBlocks(type, data) {
    console.debug("[CustomizerBridge] Notifying blocks with DOM event:", type, data);

    // Send postMessage to all iframes (broaden selector to include all embed types)
    const iframes = document.querySelectorAll('iframe[src*="customizer"], iframe[src*="customize"], iframe[src*="embed"]');
    iframes.forEach((iframe) => {
      if (iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: type,
            ...data,
          },
          "*",
        );
      }
    });

    // Dispatch custom events for React components with fromBridge flag
    document.dispatchEvent(
      new CustomEvent(`customizer:${type}`, {
        detail: { ...data, __fromBridge: true },
      }),
    );
  }

  // Layer list management methods
  updateLayerList(data) {
    console.debug("[CustomizerBridge] updateLayerList called with data:", data);

    const layerListContainer = document.getElementById("layer-list-container");
    const layerListWrapper = document.getElementById("customizer-layer-list");

    console.debug("[CustomizerBridge] Layer list elements found:", {
      hasContainer: !!layerListContainer,
      hasWrapper: !!layerListWrapper,
    });

    if (!layerListContainer || !layerListWrapper) {
      console.warn("[CustomizerBridge] Layer list elements not found in DOM");
      return;
    }

    const { layers = [], currentMotif } = data;

    console.debug("[CustomizerBridge] Processing layers:", {
      layerCount: layers.length,
      layers: layers.map((l) => ({ id: l.id, type: l.type, content: l.content })),
    });

    // Show/hide layer list based on whether there are layers
    if (layers.length > 0) {
      layerListWrapper.style.display = "block";
      console.debug("[CustomizerBridge] Showing layer list");
    } else {
      layerListWrapper.style.display = "none";
      console.debug("[CustomizerBridge] Hiding layer list (no layers)");
      return;
    }

    layerListContainer.innerHTML = "";

    // Sort layers by zIndex to display in correct order (highest first)
    const sortedLayers = [...layers].sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0));

    let textLayerCount = 0;
    let imageLayerCount = 0;

    sortedLayers.forEach((layer) => {
      if (layer.type === "text") textLayerCount++;
      else if (layer.type === "image") imageLayerCount++;

      const layerElement = this.createLayerElement(layer, textLayerCount, imageLayerCount);
      layerListContainer.appendChild(layerElement);
    });

    console.debug("[CustomizerBridge] Layer list updated with", sortedLayers.length, "layers");
  }

  createLayerElement(layer, textCount, imageCount) {
    const layerDiv = document.createElement("div");
    layerDiv.className = "layer-item";
    layerDiv.dataset.layerId = layer.id;
    layerDiv.style.cssText = `
      position: relative;
      width: 80px;
      height: 80px;
      border: 2px solid #e5e7eb;
      border-radius: 8px;
      background: #f9fafb;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 8px;
    `;

    // Create content container
    const contentDiv = document.createElement("div");
    contentDiv.style.cssText = `
      width: 60px;
      height: 60px;
      border-radius: 4px;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      background: white;
      margin-bottom: 4px;
    `;

    if (layer.type === "text") {
      const textPreview = document.createElement("div");
      textPreview.textContent = layer.content || `Text ${textCount}`;
      textPreview.style.cssText = `
        font-size: 10px;
        font-weight: bold;
        color: #374151;
        text-align: center;
        line-height: 1.2;
        word-break: break-word;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
      `;
      contentDiv.appendChild(textPreview);
    } else if (layer.type === "image") {
      // Show image thumbnail instead of placeholder
      const img = document.createElement("img");
      img.src = layer.content; // Use layer.content instead of layer.src
      img.style.cssText = `
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 2px;
      `;
      img.onerror = () => {
        contentDiv.innerHTML = `<div style="font-size: 10px; color: #6b7280;">Image ${imageCount}</div>`;
      };
      contentDiv.appendChild(img);
    } else {
      const placeholder = document.createElement("div");
      placeholder.textContent = layer.type === "image" ? `Image ${imageCount}` : "Layer";
      placeholder.style.cssText = "font-size: 10px; color: #6b7280;";
      contentDiv.appendChild(placeholder);
    }

    layerDiv.appendChild(contentDiv);

    // Add delete button with better positioning
    const deleteBtn = document.createElement("button");
    deleteBtn.innerHTML = "×";
    deleteBtn.style.cssText = `
      position: absolute;
      top: -6px;
      right: -6px;
      width: 18px;
      height: 18px;
      background: #ef4444;
      color: white;
      border: none;
      border-radius: 50%;
      font-size: 12px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      line-height: 1;
      z-index: 10;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    `;

    deleteBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      this.showDeleteConfirmation(layer.id);
    });

    layerDiv.appendChild(deleteBtn);

    // Add click handler for selection with two-way communication
    layerDiv.addEventListener("click", () => {
      this.selectLayer(layer.id);
    });

    return layerDiv;
  }

  // Update layer selection visual state
  updateLayerSelection(layerId) {
    console.debug("[CustomizerBridge] updateLayerSelection called with layerId:", layerId);

    // Track the currently selected layer for quick actions
    this.currentSelectedLayerId = layerId;
    
    // Update quick action button states
    this.updateQuickActionButtons();

    // Remove active state from all layers
    const allLayerItems = document.querySelectorAll("[data-layer-id]");
    allLayerItems.forEach((item) => {
      item.style.borderColor = "#e5e7eb";
      item.style.background = "#f9fafb";
    });

    // Add active state to selected layer
    if (layerId) {
      const selectedLayer = document.querySelector(`[data-layer-id="${layerId}"]`);
      if (selectedLayer) {
        selectedLayer.style.borderColor = "#3b82f6";
        selectedLayer.style.background = "#eff6ff";
      }
    }
  }

  selectLayer(layerId) {
    console.debug("[CustomizerBridge] selectLayer called with layerId:", layerId);

    // Update visual selection in layer list
    document.querySelectorAll(".layer-item").forEach((item) => {
      item.style.borderColor = "#e5e7eb";
      item.style.background = "#f9fafb";
    });

    const selectedItem = document.querySelector(`[data-layer-id="${layerId}"]`);
    if (selectedItem) {
      selectedItem.style.borderColor = "#3b82f6";
      selectedItem.style.background = "#eff6ff";
    }

    // Immediately update button states (don't wait for React round-trip)
    this.currentSelectedLayerId = layerId;
    this.updateQuickActionButtons();

    // Notify React customizer in both native and iframe modes
    // 1) Native embed: DOM event
    document.dispatchEvent(new CustomEvent("native:layer:select", { detail: { layerId } }));
    // 2) Iframe embed: postMessage to customizer iframes
    try {
      this.sendToTargets("layer:select", { layerId });
    } catch (e) {
      console.debug("[CustomizerBridge] sendToTargets failed for layer:select", e);
    }
  }
  showDeleteConfirmation(layerId) {
    // 1) Försök använda den befintliga Liquid-modellen först
    const modal = document.getElementById("layer-delete-modal");

    if (modal) {
      // a) Läs texter från data-attribut (med fallback)
      const get = (k, fb) => {
        const v = modal.dataset?.[k];
        return typeof v === "string" && v.trim() ? v.trim() : fb;
      };
      const title = get("modalTitle", "Radera lager");
      const body = get("modalBody", "Är du säker på att du vill radera detta lager? Denna åtgärd kan inte ångras.");
      const confirm = get("modalConfirm", "Radera");
      const cancel = get("modalCancel", "Avbryt");

      // b) Sätt texter i modalen
      const titleEl = modal.querySelector(".modal-content h3");
      const bodyEl = modal.querySelector(".modal-content p");
      const cancelBtn = modal.querySelector("#cancel-delete-btn");
      const confirmBtn = modal.querySelector("#confirm-delete-btn");

      if (titleEl) titleEl.textContent = title;
      if (bodyEl) bodyEl.textContent = body;
      if (cancelBtn) cancelBtn.textContent = cancel;
      if (confirmBtn) confirmBtn.textContent = confirm;

      // c) Visa modalen
      modal.style.display = "flex";

      // d) Rensa gamla lyssnare och bind om (så vi inte dubblar)
      const restore = () => {
        modal.style.display = "none";
      };

      // Ta bort ev. gamla handlare genom att byta ut noder (enkel reset-teknik)
      const replaceWithClone = (btn) => {
        if (!btn) return null;
        const clone = btn.cloneNode(true);
        btn.parentNode.replaceChild(clone, btn);
        return clone;
      };
      const cancelBtnFresh = replaceWithClone(cancelBtn);
      const confirmBtnFresh = replaceWithClone(confirmBtn);

      cancelBtnFresh?.addEventListener("click", restore, { once: true });
      modal.addEventListener(
        "click",
        (e) => {
          if (e.target === modal) restore();
        },
        { once: true },
      );

      confirmBtnFresh?.addEventListener(
        "click",
        () => {
          // DOM-event för native-embed
          document.dispatchEvent(new CustomEvent("native:layer:delete", { detail: { layerId } }));
          // postMessage till ev. iframes
          try {
            this.sendToTargets("layer:delete", { layerId });
          } catch {}
          restore();
        },
        { once: true },
      );

      return; // ✅ Vi använder Liquid-modellen, klart
    }

    // 2) Fallback: om Liquid-modalen inte finns, använd tidigare “on-the-fly” modal
    //    (tunn variant, inga hårdkodade ids som krockar med Liquid)
    const backdrop = document.createElement("div");
    backdrop.style.cssText = `
      position: fixed; inset: 0; background: rgba(0,0,0,.5);
      display: flex; align-items: center; justify-content: center; z-index:10000;
    `;
    const box = document.createElement("div");
    box.style.cssText = `
      background: #fff; padding: 24px; border-radius: 8px; max-width: 400px; margin: 20px;
      box-shadow: 0 10px 25px rgba(0,0,0,.1);
    `;
    box.innerHTML = `
      <h3 style="margin:0 0 16px 0;font-size:18px;font-weight:600;color:#111827">Radera lager</h3>
      <p style="margin:0 0 24px;color:#6b7280;line-height:1.5">Är du säker på att du vill radera detta lager? Denna åtgärd kan inte ångras.</p>
      <div style="display:flex;gap:12px;justify-content:flex-end">
        <button type="button" class="x-cancel" style="padding:8px 16px;border-radius:6px;border:1px solid #d1d5db;background:#fff;color:#374151;font-size:14px">Avbryt</button>
        <button type="button" class="x-confirm" style="padding:8px 16px;border-radius:6px;border:none;background:#ef4444;color:#fff;font-size:14px">Radera</button>
      </div>
    `;
    backdrop.appendChild(box);
    document.body.appendChild(backdrop);

    const close = () => backdrop.remove();
    box.querySelector(".x-cancel")?.addEventListener("click", close, { once: true });
    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop) close();
    });
    box.querySelector(".x-confirm")?.addEventListener(
      "click",
      () => {
        document.dispatchEvent(new CustomEvent("native:layer:delete", { detail: { layerId } }));
        try {
          this.sendToTargets("layer:delete", { layerId });
        } catch {}
        close();
      },
      { once: true },
    );
  }
}

// Initialize bridge when DOM is ready
(() => {
  const initBridge = () => {
    if (!window.shopifyCustomizerBridge) {
      console.debug("[Bridge] Creating ShopifyCustomizerBridge instance");
      window.shopifyCustomizerBridge = new ShopifyCustomizerBridge();
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initBridge);
  } else {
    initBridge();
  }
})();

/* === PATCH (säker DOM + variant-sync före /cart/add.js) === */
(() => {
  const whenReady = (fn) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn, { once: true });
    } else {
      fn();
    }
  };

  const runForForm = (form) => {
    if (!form || form.dataset.bridgeEnsureVariant) return;
    form.dataset.bridgeEnsureVariant = "1";

    const findVariantBySize = (label) => {
      try {
        return window.shopifyCustomizerBridge?.findVariantBySize?.(label) || null;
      } catch {
        return null;
      }
    };

    const getWantedSize = () => {
      const data = window.CustomizerNativeLoader?.getDesignData?.() || {};
      const motif = (data.motifs && data.motifs[0]) || {};
      return motif.selectedSize || data.selectedSize || null;
    };

    const selectVariantId = (variantId) => {
      if (window.shopifyCustomizerBridge?.selectVariantId) {
        // använder din klass’ metod (uppdaterar hidden, script + triggar change)
        window.shopifyCustomizerBridge.selectVariantId(variantId);
        return;
      }
      // robust fallback om metoden inte finns
      const sel = form.querySelector('select[name="id"]');
      if (sel && sel.value !== String(variantId)) {
        sel.value = String(variantId);
        sel.dispatchEvent(new Event("change", { bubbles: true }));
      }
      const hid = form.querySelector('input[type="hidden"][name="id"]');
      if (hid && hid.value !== String(variantId)) hid.value = String(variantId);
      document.dispatchEvent(new CustomEvent("variant:change", { detail: { id: Number(variantId) } }));
    };

    let posting = false;
    const ensureVariantBeforeAdd = (evt) => {
      try {
        // Try frame-aware matching first using native loader
        const designData = window.CustomizerNativeLoader?.getDesignData?.();
        if (designData && window.CustomizerNativeLoader?.findMatchingVariant) {
          const selection = {
            productType: designData.productType || "poster",
            size: designData.size?.label,
            frame: designData.frame,
            orientation: designData.orientation || "portrait",
          };
          const match = window.CustomizerNativeLoader.findMatchingVariant(selection);
          if (match?.id) {
            selectVariantId(match.id);
            console.debug("[Bridge] Forcing frame-aware variant before add.js:", match.id, "for", selection);
            return;
          }
        }

        // Fallback to size-only matching
        const wanted = getWantedSize();
        if (wanted) {
          const match = findVariantBySize(wanted);
          if (match?.id) {
            selectVariantId(match.id);
            console.debug("[Bridge] Forcing variant id before add.js:", match.id, "for size", wanted);
          } else {
            console.warn("[Bridge] No variant matched size:", wanted);
          }
        } else {
          console.warn("[Bridge] Could not read wanted size before add.js");
        }
      } catch (e) {
        console.warn("[Bridge] ensureVariantBeforeAdd error", e);
      }

      // skydda mot dubblett-submit från tema
      if (evt) {
        evt.stopImmediatePropagation?.();
        if (posting) evt.preventDefault();
        posting = true;
        setTimeout(() => (posting = false), 1500);
      }
    };

    // fånga tidigt (capture=true) före tema-script
    form.addEventListener("submit", ensureVariantBeforeAdd, true);
    const addBtn = form.querySelector('[type="submit"], button[name="add"]');
    if (addBtn) addBtn.addEventListener("click", ensureVariantBeforeAdd, true);

    // logg för kontroll
    form.addEventListener(
      "submit",
      () => {
        const idInput = form.querySelector('[name="id"]');
        console.debug("[Bridge] FINAL id in form before /cart/add.js:", idInput?.value);
      },
      true,
    );

    console.debug("[Bridge] ensureVariant hooked on form", form);
  };

  const attach = () => {
    const form =
      document.querySelector('form[action*="/cart/add"]') || document.querySelector('form[action="/cart/add"]');
    if (form) runForForm(form);
  };

  whenReady(() => {
    attach();

    // starta observern bara när vi har en Node
    const startObserverSafely = () => {
      const root = document.body || document.documentElement;
      if (!root) {
        // prova igen strax om body inte finns ännu
        setTimeout(startObserverSafely, 50);
        return;
      }
      try {
        const mo = new MutationObserver(attach);
        mo.observe(root, { childList: true, subtree: true });
      } catch (e) {
        console.debug("[Bridge] MutationObserver start failed once, retrying…", e);
        setTimeout(startObserverSafely, 50);
      }
    };

    startObserverSafely();
  });

  // --- minimal patch: orientation-only change -> propagate immediately ---

  function normalizeOrientation(v) {
    const s = String(v || "")
      .trim()
      .toLowerCase();
    if (s === "landskap" || s === "landscape" || s === "landschaft") return "landscape";
    if (s === "porträtt" || s === "portrait" || s === "porträt") return "portrait";
    return "portrait";
  }

  function extractOrientationFromTarget(t) {
    // Försök i ordning: data-option-value, value, data-orientation, id/text fallback
    return t.getAttribute?.("data-option-value") || t.value || t.getAttribute?.("data-orientation") || t.id || "";
  }

  // Lyssna på *endast* orienteringsradios. Fångar båda varianterna:
  //  - inputs markerade med data-option-name="orientation"
  //  - inputs vars namn börjar med "Orientation" (vanligt mönster i din kodbas)
  document.addEventListener("change", (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement) || t.type !== "radio") return;

    const isOrientation =
      t.matches('input[type="radio"][data-option-name="orientation"]') ||
      t.name?.toLowerCase().startsWith("orientation");

    if (!isOrientation) return;

    const raw = extractOrientationFromTarget(t);
    const orientation = normalizeOrientation(raw);

    const payload = { orientation };

    // 1) Skicka ett internt event som din app redan lyssnar på
    document.dispatchEvent(new CustomEvent("customizer:variant:change", { detail: payload }));

    // 2) Pinga native-loader direkt om funktionen finns
    window.CustomizerNativeLoader?.applyVariantChange?.(payload);
  });
})();
